import {
  createAttachmentRef,
  type AttachmentRef,
  type AttachmentStore,
  type JsonObject,
} from "@fabric-harness/sdk";
import type {
  JobsClient,
  GetRunResponse,
  RunTask,
  RunNowRequest,
  SubmitRunRequest,
} from "@databricks/sdk-jobs/v2";

export type DatabricksJobsClient = Pick<
  JobsClient,
  "runNow" | "submitRun" | "getRun" | "cancelRun" | "getRunOutput"
>;

export type DatabricksRunLifeCycleState =
  | "PENDING" | "RUNNING" | "TERMINATING" | "TERMINATED" | "SKIPPED"
  | "INTERNAL_ERROR" | "BLOCKED" | "WAITING_FOR_RETRY" | "QUEUED" | string;
export type DatabricksRunResultState =
  | "SUCCESS" | "FAILED" | "TIMEDOUT" | "CANCELED"
  | "MAXIMUM_CONCURRENT_RUNS_REACHED" | "EXCLUDED" | "SUCCESS_WITH_FAILURES" | string;

export interface DatabricksRunReceipt {
  runId: number;
  run_id: number;
  idempotencyToken: string;
  submittedAt: string;
  kind: "job" | "notebook";
  numberInJob?: number;
}

export interface DatabricksRunState {
  runId: number;
  lifeCycleState: DatabricksRunLifeCycleState;
  resultState?: DatabricksRunResultState;
  stateMessage?: string;
  runPageUrl?: string;
  startTime?: number;
  endTime?: number;
  tasks?: Array<{ runId: number; taskKey?: string; state?: DatabricksRunState }>;
  raw: Record<string, unknown>;
}

export interface DatabricksRunOutput {
  runId: number;
  notebookResult?: string;
  logs?: string;
  logsTruncated?: boolean;
  error?: string;
  metadata: Record<string, unknown>;
  raw: Record<string, unknown>;
}

export interface DatabricksJobsWaitOptions {
  pollIntervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
}

export interface RunDatabricksJobInput {
  jobId: number;
  idempotencyToken?: string;
  notebookParams?: JsonObject;
  pythonParams?: string[];
  jarParams?: string[];
}

export interface SubmitDatabricksNotebookInput {
  notebookPath: string;
  existingClusterId?: string;
  baseParameters?: JsonObject;
  runName?: string;
  idempotencyToken?: string;
}

export class DatabricksRunTimeoutError extends Error {
  readonly runId: number;
  constructor(runId: number, timeoutMs: number) {
    super(`Databricks run ${runId} did not reach a terminal state within ${timeoutMs}ms.`);
    this.name = "DatabricksRunTimeoutError";
    this.runId = runId;
  }
}

/** Raised before the Jobs API is called when a run target falls outside the configured bound. */
export class DatabricksRunNotAllowedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DatabricksRunNotAllowedError";
  }
}

/**
 * Bound for triggering existing jobs. Ids, not names: the narrow run client cannot resolve names,
 * and call-time resolution would be a TOCTOU hole. `allowAnyJobId` is the explicit opt-out.
 */
export type DatabricksJobRunPolicy =
  | { allowedJobIds: number[]; allowAnyJobId?: never }
  | { allowAnyJobId: true; allowedJobIds?: never };

/** Bound for one-off notebook submission. `allowAnyNotebookPath` is the explicit opt-out. */
export type DatabricksNotebookRunPolicy =
  | {
      /** Exact workspace notebook paths. */
      allowedNotebookPaths: string[];
      /** Workspace subtree prefixes; matched on segment boundaries only. */
      allowedNotebookPathPrefixes?: string[];
      allowAnyNotebookPath?: never;
    }
  | {
      allowedNotebookPaths?: string[];
      /** Workspace subtree prefixes; matched on segment boundaries only. */
      allowedNotebookPathPrefixes: string[];
      allowAnyNotebookPath?: never;
    }
  | {
      allowAnyNotebookPath: true;
      allowedNotebookPaths?: never;
      allowedNotebookPathPrefixes?: never;
    };

export interface DatabricksJobsOptions {
  runPolicy?: DatabricksJobRunPolicy;
  notebookPolicy?: DatabricksNotebookRunPolicy;
}

export function validateDatabricksJobRunPolicy(policy: DatabricksJobRunPolicy): void {
  if (policy.allowAnyJobId === true) return;
  const ids = policy.allowedJobIds;
  if (!Array.isArray(ids) || ids.length === 0)
    throw new Error("databricks jobs: runPolicy.allowedJobIds must be non-empty when configured.");
  for (const id of ids)
    if (!Number.isSafeInteger(id) || id < 0)
      throw new Error(
        "databricks jobs: runPolicy.allowedJobIds must contain non-negative integer job ids.",
      );
}

export function validateDatabricksNotebookRunPolicy(policy: DatabricksNotebookRunPolicy): void {
  if (policy.allowAnyNotebookPath === true) return;
  const paths = policy.allowedNotebookPaths ?? [];
  const prefixes = policy.allowedNotebookPathPrefixes ?? [];
  if (paths.length === 0 && prefixes.length === 0)
    throw new Error(
      "databricks jobs: notebookPolicy must configure a non-empty allowedNotebookPaths or allowedNotebookPathPrefixes.",
    );
  for (const [name, values] of [
    ["allowedNotebookPaths", paths],
    ["allowedNotebookPathPrefixes", prefixes],
  ] as const) {
    for (const value of values) {
      if (typeof value !== "string" || !value.startsWith("/"))
        throw new Error(
          `databricks jobs: notebookPolicy.${name} entries must be absolute workspace paths.`,
        );
      if (hasParentSegment(value))
        throw new Error(
          `databricks jobs: notebookPolicy.${name} entries cannot contain a ".." segment.`,
        );
    }
  }
}

function hasParentSegment(path: string): boolean {
  return path.split("/").includes("..");
}

function notebookPathAllowed(policy: DatabricksNotebookRunPolicy, path: string): boolean {
  if (policy.allowedNotebookPaths?.includes(path)) return true;
  return (policy.allowedNotebookPathPrefixes ?? []).some((prefix) => {
    // Normalise a trailing slash so `/Workspace/prod` and `/Workspace/prod/` bound identically.
    const normalized = prefix.length > 1 && prefix.endsWith("/") ? prefix.slice(0, -1) : prefix;
    return normalized === path || path.startsWith(`${normalized}/`);
  });
}

export class DatabricksJobs {
  private readonly runPolicy: DatabricksJobRunPolicy | undefined;
  private readonly notebookPolicy: DatabricksNotebookRunPolicy | undefined;

  constructor(
    private readonly client: DatabricksJobsClient,
    options: DatabricksJobsOptions = {},
  ) {
    if (options.runPolicy !== undefined) validateDatabricksJobRunPolicy(options.runPolicy);
    if (options.notebookPolicy !== undefined)
      validateDatabricksNotebookRunPolicy(options.notebookPolicy);
    this.runPolicy = options.runPolicy;
    this.notebookPolicy = options.notebookPolicy;
  }

  async runJob(input: RunDatabricksJobInput): Promise<DatabricksRunReceipt> {
    this.assertJobAllowed(input.jobId);
    const idempotencyToken = input.idempotencyToken ?? crypto.randomUUID();
    const request: RunNowRequest = {
      jobId: BigInt(input.jobId),
      idempotencyToken,
      ...(input.notebookParams ? { notebookParams: stringValues(input.notebookParams) } : {}),
      ...(input.pythonParams ? { pythonParams: input.pythonParams } : {}),
      ...(input.jarParams ? { jarParams: input.jarParams } : {}),
    };
    const waiter = await this.client.runNow(request);
    return receipt(waiter.runId, undefined, idempotencyToken, "job");
  }

  async submitNotebook(input: SubmitDatabricksNotebookInput): Promise<DatabricksRunReceipt> {
    this.assertNotebookAllowed(input.notebookPath);
    const idempotencyToken = input.idempotencyToken ?? crypto.randomUUID();
    const request: SubmitRunRequest = {
      idempotencyToken,
      runName: input.runName ?? `fabric-harness:${input.notebookPath}`,
      tasks: [{
        taskKey: "notebook",
        ...(input.existingClusterId
          ? { spec: { $case: "existingClusterId" as const, existingClusterId: input.existingClusterId } }
          : {}),
        task: {
          $case: "notebookTask",
          notebookTask: {
            notebookPath: input.notebookPath,
            source: "WORKSPACE",
            ...(input.baseParameters ? { baseParameters: stringValues(input.baseParameters) } : {}),
          },
        },
      }],
    };
    const waiter = await this.client.submitRun(request);
    return receipt(waiter.runId, undefined, idempotencyToken, "notebook");
  }

  async get(runId: number): Promise<DatabricksRunState> {
    return parseRunState(runId, await this.client.getRun({ runId: BigInt(runId) }));
  }

  async wait(runId: number, options: DatabricksJobsWaitOptions = {}): Promise<DatabricksRunState> {
    const timeoutMs = options.timeoutMs ?? 600_000;
    const deadline = Date.now() + timeoutMs;
    while (true) {
      if (options.signal?.aborted)
        throw options.signal.reason ?? new DOMException("The operation was aborted.", "AbortError");
      const state = await this.get(runId);
      if (isTerminalLifeCycle(state.lifeCycleState)) return state;
      const remaining = deadline - Date.now();
      if (remaining <= 0) throw new DatabricksRunTimeoutError(runId, timeoutMs);
      await abortableSleep(Math.min(options.pollIntervalMs ?? 5_000, remaining), options.signal);
    }
  }

  async cancel(runId: number): Promise<void> {
    await this.client.cancelRun({ runId: BigInt(runId) });
  }

  async getOutput(runId: number): Promise<DatabricksRunOutput> {
    const response = await this.client.getRunOutput({ runId: BigInt(runId) });
    const notebook = response.result?.$case === "notebookOutput"
      ? response.result.notebookOutput
      : undefined;
    return {
      runId,
      ...(typeof notebook?.result === "string" ? { notebookResult: notebook.result } : {}),
      ...(typeof response.logs === "string" ? { logs: response.logs } : {}),
      ...(typeof response.logsTruncated === "boolean" ? { logsTruncated: response.logsTruncated } : {}),
      ...(typeof response.error === "string" ? { error: response.error } : {}),
      metadata: plainRecord(response.metadata),
      raw: plainRecord(response),
    };
  }

  async getOutputs(runId: number): Promise<DatabricksRunOutput[]> {
    const state = await this.get(runId);
    const taskRunIds = state.tasks?.map((task) => task.runId) ?? [];
    return Promise.all(
      (taskRunIds.length ? taskRunIds : [runId]).map((taskRunId) => this.getOutput(taskRunId)),
    );
  }

  async exportOutput(runId: number, store: AttachmentStore, scope: string): Promise<AttachmentRef> {
    const output = await this.getOutputs(runId);
    const bytes = new TextEncoder().encode(`${JSON.stringify({ runId, tasks: output }, null, 2)}\n`);
    const ref = await createAttachmentRef({
      id: `databricks-run-${runId}`,
      mimeType: "application/json",
      bytes,
      filename: `databricks-run-${runId}.json`,
    });
    await store.put({ scope, ref, bytes });
    return ref;
  }

  /** Bound checked before `runNow`, so an out-of-policy id never reaches the workspace. */
  private assertJobAllowed(jobId: number): void {
    const policy = this.runPolicy;
    if (!policy || policy.allowAnyJobId === true) return;
    if (!(policy.allowedJobIds ?? []).includes(jobId))
      throw new DatabricksRunNotAllowedError(
        `databricks jobs: job ${jobId} is not in the configured run allowlist.`,
      );
  }

  /** Bound checked before `submitRun`, so an out-of-policy notebook is never executed. */
  private assertNotebookAllowed(notebookPath: string): void {
    const policy = this.notebookPolicy;
    if (!policy || policy.allowAnyNotebookPath === true) return;
    if (hasParentSegment(notebookPath) || !notebookPathAllowed(policy, notebookPath))
      throw new DatabricksRunNotAllowedError(
        `databricks jobs: notebook path ${JSON.stringify(notebookPath)} is not in the configured run allowlist.`,
      );
  }
}

export function databricksJobs(
  client: DatabricksJobsClient,
  options?: DatabricksJobsOptions,
): DatabricksJobs {
  return new DatabricksJobs(client, options);
}

function receipt(
  runIdValue: bigint | undefined,
  numberInJobValue: bigint | undefined,
  idempotencyToken: string,
  kind: DatabricksRunReceipt["kind"],
): DatabricksRunReceipt {
  if (runIdValue === undefined)
    throw new Error("Databricks Jobs response did not contain run_id.");
  const runId = safeNumber(runIdValue, "run_id");
  return {
    runId,
    run_id: runId,
    idempotencyToken,
    submittedAt: new Date().toISOString(),
    kind,
    ...(numberInJobValue !== undefined
      ? { numberInJob: safeNumber(numberInJobValue, "number_in_job") }
      : {}),
  };
}

function parseRunState(runId: number, response: GetRunResponse): DatabricksRunState {
  const state = response.state;
  const tasks = (response.tasks ?? []).flatMap((task: RunTask) => {
    if (task.runId === undefined) return [];
    const taskRunId = safeNumber(task.runId, "task.run_id");
    return [{
      runId: taskRunId,
      ...(task.taskKey ? { taskKey: task.taskKey } : {}),
      ...(task.state
        ? {
            state: parseRunState(taskRunId, {
              runId: task.runId,
              state: task.state,
              runPageUrl: task.runPageUrl,
              startTime: task.startTime,
              endTime: task.endTime,
            }),
          }
        : {}),
    }];
  });
  return {
    runId,
    lifeCycleState: state?.lifeCycleState ?? "UNKNOWN",
    ...(state?.resultState ? { resultState: state.resultState } : {}),
    ...(state?.stateMessage ? { stateMessage: state.stateMessage } : {}),
    ...(response.runPageUrl ? { runPageUrl: response.runPageUrl } : {}),
    ...(response.startTime !== undefined ? { startTime: safeNumber(response.startTime, "start_time") } : {}),
    ...(response.endTime !== undefined ? { endTime: safeNumber(response.endTime, "end_time") } : {}),
    ...(tasks.length ? { tasks } : {}),
    raw: plainRecord(response),
  };
}

function isTerminalLifeCycle(state: string): boolean {
  return state === "TERMINATED" || state === "SKIPPED" || state === "INTERNAL_ERROR";
}

function safeNumber(value: bigint, field: string): number {
  const number = Number(value);
  if (!Number.isSafeInteger(number))
    throw new Error(`Databricks Jobs ${field} exceeded JavaScript's safe integer range.`);
  return number;
}

function stringValues(value: JsonObject): Record<string, string> {
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, typeof item === "string" ? item : JSON.stringify(item)]),
  );
}

function plainRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return plainValue(value) as Record<string, unknown>;
}

function plainValue(value: unknown): unknown {
  if (typeof value === "bigint") return safeNumber(value, "response identifier");
  if (Array.isArray(value)) return value.map(plainValue);
  if (value && typeof value === "object")
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, plainValue(item)]));
  return value;
}

function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (!signal) return new Promise((resolve) => setTimeout(resolve, ms));
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, ms);
    const abort = () => {
      clearTimeout(timer);
      reject(signal.reason ?? new DOMException("The operation was aborted.", "AbortError"));
    };
    signal.addEventListener("abort", abort, { once: true });
  });
}
